import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import {
  GetInvitationsFilters,
  GetInvitationsParams,
  GetInvitationsResponse,
  InvitationRow,
} from '../types/invitation.type';
import { ADMIN_INVITATIONS_API_PATHS } from '../constants/invitations-api-paths.constants';

export type GetInvitationsRequest = Omit<GetInvitationsParams, 'filters'> & { filters: GetInvitationsFilters };

type UseAdminGetInvitationsResult = {
  isLoading: boolean;
  isRefetching: boolean;
  totalCount: number;
  invitations: InvitationRow[];
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetInvitationsResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  totalCount: 0,
  invitations: [],
};

export function useAdminGetInvitations({
  page,
  limit,
  sortBy,
  sortDirection,
  filters,
}: GetInvitationsRequest): UseAdminGetInvitationsResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const serializedFilters = JSON.stringify(filters);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<GetInvitationsResponse>(ADMIN_INVITATIONS_API_PATHS.GET_INVITATIONS, {
        params: { page, limit, sortBy, sortDirection, filters: serializedFilters },
      });

      setResult({
        isLoading: false,
        isRefetching: false,
        totalCount: response.totalCount,
        invitations: response.invitations,
      });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des invitations'),
        { variant: 'error' }
      );
      setResult({ isLoading: false, isRefetching: false, totalCount: 0, invitations: [] });
    }
  }, [page, limit, sortBy, sortDirection, serializedFilters, enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
